import { Button } from "@fluentui/react-components";
import {
  ChevronDoubleLeftRegular,
  ChevronDownFilled,
  ChevronUpFilled,
  PanelLeftExpandRegular,
} from "@fluentui/react-icons";
import classNames from "classnames";
import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useAppRouteParams } from "../../../AppRoutes";
import {
  MAXIMUM_RESIZE_SIDE_MENU_WIDTH,
  MINIMUM_RESIZE_SIDE_MENU_WIDTH,
} from "../../../const";
import { Sheet } from "../../../domain/Sheet";
import { useResizeHandler } from "../../../hooks/useResizeHandler";
import { TmpMenuSetDivider, TmpMenuSetOptimizeSummary } from "../helpers";
import { useAppSideNavOpenState } from "../providers/AppSideNavOpenStateProvider";

import { AppSideNavNameTooltip } from "../AppSideNavNameTooltip";
import { SheetsMenu } from "./SheetsMenu";
import { ViewsMenu } from "./ViewsMenu";

import styles from "./index.module.css";

import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { useAuthorizedSheets } from "@/hooks/useAuthorizedCollections";
import { translation } from "@/i18n";
import { nsSideNav } from "@/i18n/locales/sideNav";
import { buildVersionEntryPath } from "@/utils/versionPath";

const useTranslation = translation.use(nsSideNav);

/**
 * @description メニュ-画面左に常にあるメニュー. バージョン画面や、トップページで、表示内容が変わる
 *
 */
export const AppSideNavLocal: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizations } = useAppOrgsAndInvitedOrgs();
  const { versionId, organizationId } = useAppRouteParams();
  const organization = organizations.find(
    (org) => org.org_id === organizationId
  );
  const { versions } = useVersions();
  const version = versions.find((v) => v.id === versionId);
  const [sheets, isLoadedSheets] = useAuthorizedSheets(versionId);

  const {
    isGlobalOpen,
    isLocalOpen,
    isToggleable,
    isLocalRenderable,
    localWidth,
    setIsGlobalOpen,
    setIsLocalOpen,
    setLocalWidth,
  } = useAppSideNavOpenState();

  const rootRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const onResizeHandlerMouseDown = useResizeHandler({
    type: "right",
    targetRef: rootRef,
    onResizeStart: () => {
      setIsResizing(true);
    },
    onResize: (width) => {
      if (
        width <= MINIMUM_RESIZE_SIDE_MENU_WIDTH ||
        width >= MAXIMUM_RESIZE_SIDE_MENU_WIDTH
      )
        return;
      setLocalWidth(width);
    },
    onResizeEnd: () => {
      setIsResizing(false);
    },
  });

  const [isOpenChildItems, setIsOpenChildItems] = useState(false);
  const resultScoreSummary: Sheet | null = useMemo(
    () =>
      sheets.find(
        ({ sheetOptimizeType }) =>
          sheetOptimizeType === "result_score_summaries"
      ) ?? null,
    [sheets]
  );

  const isVisibleContextMenu = organization?.is_sheet_enabled === true;

  /**
   * NOTE
   * versionIdがURLに入っていない時 or ファイル最適化バージョンの時は表示しない。
   * 描画可否の判定は Provider (AppSideNavOpenStateProvider) に集約している。
   */
  if (!isLocalRenderable || !version) {
    return null;
  }
  return (
    <aside
      role="navigation"
      aria-label={t.sideNav.local.ariaLabel()}
      aria-hidden={!isLocalOpen}
      className={classNames(styles.root, {
        [styles.isResizing]: isResizing,
        [styles.isClosed]: !isLocalOpen,
      })}
      style={{
        width: `${isLocalOpen ? localWidth : 0}px`,
      }}
      ref={rootRef}
    >
      <div className={styles.firstMenu}>
        <div
          className={styles.content}
          onClick={() => {
            navigate(
              buildVersionEntryPath(
                organizationId,
                versionId,
                version.initialViewId
              )
            );
          }}
        >
          <div className={styles.textWithTooltip}>
            <AppSideNavNameTooltip
              content={version.title}
              relationship="description"
            >
              <div className={styles.title}>{version.title}</div>
            </AppSideNavNameTooltip>
          </div>
          <div className={styles.rightButtons}>
            {isVisibleContextMenu ? (
              <Button
                icon={
                  isOpenChildItems ? <ChevronUpFilled /> : <ChevronDownFilled />
                }
                className={styles.rightButtons}
                appearance="transparent"
                onMouseDown={() => {
                  setIsOpenChildItems((state) => !state);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            ) : null}
          </div>
        </div>
        {isOpenChildItems && isVisibleContextMenu ? (
          <div className={styles.summaryContainer}>
            <div className={styles.tmpMenuSetOptimizeSummary}>
              <TmpMenuSetOptimizeSummary version={version} />
              {resultScoreSummary ? (
                <div className={styles.link}>
                  <a
                    href={`#/organizations/${organizationId}/versions/${versionId}/sheets/${resultScoreSummary.id}`}
                  >
                    {t.sideNav.versionSummary.detailLink()}
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <TmpMenuSetDivider />
      <div className={styles.secondRoot}>
        <div className={styles.secondMenu}>
          <div className={styles.divider}>
            <ViewsMenu />
          </div>
          <SheetsMenu />
        </div>
      </div>
      <TmpMenuSetDivider />
      {isToggleable ? (
        <div className={styles.thirdRoot}>
          {!isGlobalOpen ? (
            <Button
              appearance="subtle"
              icon={<PanelLeftExpandRegular />}
              aria-label={t.sideNav.global.openButtonAriaLabel()}
              className={classNames(
                styles.footerButton,
                styles.footerButtonExpand,
                styles.footerButtonLeft
              )}
              onClick={() => setIsGlobalOpen(true)}
            />
          ) : (
            <span aria-hidden />
          )}
          <Button
            appearance="subtle"
            icon={<ChevronDoubleLeftRegular />}
            aria-label={t.sideNav.local.closeButtonAriaLabel()}
            className={classNames(
              styles.footerButton,
              styles.footerButtonExpand,
              styles.footerButtonRight
            )}
            onClick={() => setIsLocalOpen(false)}
          />
        </div>
      ) : null}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={localWidth}
        aria-valuemin={MINIMUM_RESIZE_SIDE_MENU_WIDTH}
        aria-valuemax={MAXIMUM_RESIZE_SIDE_MENU_WIDTH}
        className={styles.rootResizeHandler}
        onMouseDown={() => {
          if (!isLocalOpen) return;
          onResizeHandlerMouseDown();
        }}
      />
    </aside>
  );
};
